import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';  
import { selectUser, logout, isTokenExpired } from '../Redux/currentUserSlice';
import { persistor } from '../Redux/store';
import IP_URL from './connect';
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

// ================= Styled Components =================
const Main = styled.div`
  width: 20vw;
  background: linear-gradient(180deg, #f8f9fa, #dfe6f1);
  min-height: 90vh;
  position: fixed;
  top: 60px;
  left: ${({ sidebarOpen }) => (sidebarOpen ? '0' : '-100%')};
  transition: all 0.3s ease-in-out;
  z-index: 1100;
  border-right: 2px solid #c5d3f0;
  box-shadow: 4px 0 10px rgba(0,0,0,0.1);
  padding-top: 1rem;
  border-radius: 8px;

  @media(max-width: 700px) {
    width: 65vw;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 5%;
`;

const Section = styled.div`
  margin-top: 0.6rem;
`;

const Button = styled.div`
  margin: 5px 0;
  padding: 10px;
  background-color: white;
  border-radius: 5px;
  border: 1px solid rgb(75, 135, 233);
  color: #333;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background-color: rgb(75, 135, 233);
    color: white;
    transform: translateY(-1px);
  }
`;

const ToggleButton = styled.button`
  padding: 12px;
  width: 100%;
  font-size: 1rem;
  background-color: rgb(27, 133, 225); 
  color: white;
  border: none;
  cursor: pointer;
  border-radius: 0;
  text-align: left;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgb(29, 91, 235);
  }
`;

const ButtonSection = styled.div`
  position: absolute;
  bottom: 70px;
  left: 2vw;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  font-size: 0.95rem;
  display: block;
  width: 100%;
`;

// ================= Main Component =================
const Sidebar = ({ sidebarOpen }) => {
  const [showDashboardButtons, setShowDashboardButtons] = useState(false);
  const [showMeterButtons, setShowMeterButtons] = useState(false);
  const [showReportButtons, setShowReportButtons] = useState(false);
  const [showAccountButtons, setShowAccountButtons] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showBillingButtons, setShowBillingButtons] = useState(false);
  const [showSimButtons, setShowSimButtons] = useState(false);

  const user = useSelector(selectUser);
  const { token } = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Logout Function
  const handleLogout = async () => {
    try {
      const response = await fetch(`${IP_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        toast.error("Logout request failed.");
      } else {
        toast.success("Logged out successfully.");
      }
    } catch (err) {
      toast.error("Network error during logout.");
    } finally {
      dispatch(logout());
      await persistor.purge();
      navigate('/');
    }
  };

  // ✅ Auto logout if token expires
  useEffect(() => {
    if (isTokenExpired(token)) {
      handleLogout();
      toast.warn("⚠️ Session expired. Please log in again.");
    }
  }, [token]);

  return (
    <>
      <Main sidebarOpen={sidebarOpen}>
        {/* Dashboard */}
        <Section>
          <ToggleButton onClick={() => setShowDashboardButtons(!showDashboardButtons)}>
            Overview
          </ToggleButton>
          {showDashboardButtons && (
            <Container>
              {user?.role === 'super_admin' && (
                <Button><StyledLink to='/admin-dashboard'>Dashboard</StyledLink></Button>
              )}
              {user?.role === 'admin' && (
                <Button><StyledLink to='/Dashboard'>Dashboard</StyledLink></Button>
              )}
              <Button><StyledLink to='/Account'>Profile</StyledLink></Button>
              <Button onClick={handleLogout}>Logout</Button>
            </Container>
          )}
        </Section>

        {/* Meters */}
        <Section>
          <ToggleButton onClick={() => setShowMeterButtons(!showMeterButtons)}>Meters</ToggleButton>
          {showMeterButtons && (
            <Container>
              {user?.role === 'admin' && (
             <>
                  <Button><StyledLink to='/meterData'>Meter Data</StyledLink></Button>
                <Button><StyledLink to ='/org-mapview'>Map View</StyledLink></Button>
             </>
              )}
              {user?.role === 'super_admin' && (
                <>
                  <Button><StyledLink to='/meter-report'>All meters</StyledLink></Button>
                  <Button><StyledLink to='/map'>Map View</StyledLink></Button>
                  <Button><StyledLink to='/admin-meter-data'>Original Data</StyledLink></Button>
                </>
              )}
            </Container>
          )}
        </Section>


        {/* Reports */}
        <Section>
          <ToggleButton onClick={() => setShowReportButtons(!showReportButtons)}>Reports</ToggleButton>
          {showReportButtons && (
            <Container>
              {user?.role === 'super_admin' && (
                <Button><StyledLink to='/userReport'>User Report</StyledLink></Button>
              )}
              {user?.role === 'admin' && (
               <>
                <Button><StyledLink to='/consumption-report'>Consumption</StyledLink></Button>
                <Button><StyledLink to='/waterbalance'>Water Balance</StyledLink></Button>
               </>
              )}
            </Container>
          )}
        </Section>

                {/* Billing */}
        {user?.role === 'admin' && (
          <Section>
            <ToggleButton onClick={() => setShowBillingButtons(!showBillingButtons)}>Billing</ToggleButton>
            {showBillingButtons && (
              <Container>
                <Button><StyledLink to='/org-payments'>Prepaid</StyledLink></Button>
              </Container>
            )}
          </Section>
        )}


        {/* Account & Controls */}
        {user?.role === "super_admin" && (
          <>
            <Section>
              <ToggleButton onClick={() => setShowAccountButtons(!showAccountButtons)}>Account</ToggleButton>
              {showAccountButtons && (
                <Container>
                  <Button><StyledLink to='/organizations'>Organizations</StyledLink></Button>
                  <Button><StyledLink to='/add-organization'>Add Account</StyledLink></Button>
                </Container>
              )}
            </Section>

            <Section>
              <ToggleButton onClick={() => setShowControls(!showControls)}>Control</ToggleButton>
              {showControls && (
                <Container>
                  <Button><StyledLink to='/payments'>Mpesa Payments</StyledLink></Button>
                  <Button><StyledLink to='/command-center'>Command Center</StyledLink></Button>
                  <Button><StyledLink to='/command-status'>Command Status</StyledLink></Button>
                  <Button><StyledLink to='/register-url'>Register URL</StyledLink></Button>
                  <Button><StyledLink to='/business-panel'>Busines Panel</StyledLink></Button>
                </Container>
              )}
            </Section>
          </>
        )}

{/**SIM management */}
{user?.role === 'super_admin' && (
  <>
   <Section>
    <ToggleButton onClick={() => setShowSimButtons(!showSimButtons)}>SIM Management</ToggleButton>
    {showSimButtons && (
      <Container>
        <Button><StyledLink to='/sim-list'>SIM List</StyledLink></Button>
        <Button><StyledLink to='/add-sim'>Add SIM</StyledLink></Button>
      </Container>
    )}
   </Section>
  </>
)}

        {/* Profile Shortcut */}
        <ButtonSection>
          <Button>
            <StyledLink to='/account'>Profile</StyledLink>
          </Button>
        </ButtonSection>
      </Main>

   <ToastContainer/>
    </>
  );
};

export default Sidebar;
